import {
  useChannelActionContext,
  useChannelPreviewInfo,
  useChannelStateContext,
  useChatContext,
} from 'stream-chat-react';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { MessageResponse } from 'stream-chat';
import { IconButton } from '@mui/material';
import { Close, ReadMore } from '@mui/icons-material';
import { LayoutStage } from './StreamChat';
import { SearchComponent } from '../common/Search';
import { fulldate } from '@/utilities/dateUtils';
import { useTranslation } from 'react-i18next';

export default function StreamSearchPanel({
  setLayout,
  setId,
}: {
  setLayout: Dispatch<SetStateAction<LayoutStage>>;
  setId?: Dispatch<SetStateAction<string | undefined>>;
}) {
  const { client } = useChatContext();
  const { jumpToMessage } = useChannelActionContext();
  const { channel } = useChannelStateContext();
  const { displayTitle } = useChannelPreviewInfo({
    channel,
  });
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MessageResponse[] | null>(null);

  const handleSearch = async () => {
    if (!channel?.cid || !query.trim()) {
      setResults([]);
      return;
    }
    try {
      const channelFilters = { cid: { $in: [channel.cid] } };
      const messageFilters = { text: { $autocomplete: query }, parent_id: { $exists: false } };
      const { results: searchResults } = await client.search(channelFilters, messageFilters, {
        sort: [{ created_at: -1 }],
        limit: 25,
      });
      setResults(searchResults.map((item) => item.message));
    } catch (err) {
      console.error('Error searching messages:', err);
    }
  };

  useEffect(() => {
    if (query.length) {
      void handleSearch();
    }
  }, [query]);

  function ResultMessage({ msg }: { msg: MessageResponse }) {
    const date = typeof msg.created_at === 'string' ? new Date(msg.created_at) : undefined;
    const formattedDate = date ? fulldate(date?.getTime() * 1000, t) : undefined;
    return (
      <div className="stream-search-result">
        <div className="stream-search-message-container">
          <div className="stream-search-name-container">
            <b>{msg.user?.name} </b>
            {formattedDate}
          </div>
          {msg.text}
        </div>
        <IconButton
          onClick={async () => {
            if (setId) {
              setId(msg.id);
              setLayout('channel');
            }
            await jumpToMessage(msg.id);
          }}
          className="stream-search-jump-to-message"
        >
          <ReadMore />
        </IconButton>
      </div>
    );
  }

  return (
    <div className="stream-search-panel">
      <div className="stream-search-header">
        <div className="stream-search-header-infos">
          <div className="stream-search-title">{t('streamChat.Search')}</div>
          <div className="stream-search-info">{displayTitle}</div>
        </div>
        <IconButton
          onClick={() => {
            setLayout('channel');
            if (setId) setId(undefined);
          }}
        >
          <Close />
        </IconButton>
      </div>
      <SearchComponent
        searchText={query}
        setSearchText={setQuery}
        showHistory={false}
        placeholder={t('streamChat.custom.searchPlaceHolder')}
      />
      {results !== null ? (
        results.length ? (
          <div className="stream-search-result-container">
            {results.map((msg) => (
              <ResultMessage key={msg.id} msg={msg} />
            ))}
          </div>
        ) : (
          <div>{t('general.noResult')}</div>
        )
      ) : (
        <></>
      )}
    </div>
  );
}
